import { html, render, nothing, type TemplateResult } from 'lit-html';
import type * as EChartsNS from 'echarts';
import panelStyles from './pk-report-echart.css?inline';
import sharedTokens from '../pk-report-shared/pk-report-tokens.css?inline';
import { activityLogLightTheme, activityLogDarkTheme } from './activitylog-theme.js';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type PkChartTheme = 'activitylog-light' | 'activitylog-dark';

/** Detail shape for pk-chart-click and pk-chart-hover events. */
export interface PkChartEventDetail {
    dataIndex:   number;
    seriesIndex: number;
    value:       unknown;
    name:        string;
    data:        unknown;
}

// ─────────────────────────────────────────────────────────────────────────────
// <pk-report-echart>
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-echart>` — generic Apache ECharts wrapper for the PortalKit
 * Reports viz kit. v0.8
 *
 * Accepts any `EChartsOption` object via the `options` property and renders
 * the chart inside a shadow DOM container. ECharts is lazy-loaded (~280 KB)
 * on first component attachment — pages that don't use charts never pay the
 * cost.
 *
 * ActivityLog theme (`activitylog-light` / `activitylog-dark`) is registered
 * once per page and applies pack colors, PortalKit CSS token–aligned tooltip
 * styles, and consistent axis/legend typography.
 *
 * Usage:
 * ```html
 * <pk-report-echart
 *   theme="activitylog-light"
 *   height="400"
 *   empty-message="No data to display.">
 * </pk-report-echart>
 *
 * <script>
 *   const chart = document.querySelector('pk-report-echart');
 *   chart.options = { type: 'sankey', ... };
 * </script>
 * ```
 *
 * Attributes:
 *   `theme`         — `activitylog-light` (default) | `activitylog-dark`
 *   `height`        — component height in px (default 400)
 *   `empty-message` — shown when `options === null` or series is empty
 *   `loading`       — boolean; show loading spinner instead of chart
 *   `title`         — optional panel heading above chart
 *
 * Property (set imperatively; too large for HTML serialization):
 *   `options: EChartsOption | null` — full ECharts option object; setting it
 *   (re)renders the chart. Set to `null` to show empty state.
 *
 * Events:
 *   `pk-chart-click` — fired on chart element click;
 *                      `detail: PkChartEventDetail`
 *   `pk-chart-hover` — fired on chart element hover;
 *                      `detail: PkChartEventDetail`
 */
export class PkReportEchart extends HTMLElement {
    static readonly tagName = 'pk-report-echart';

    static get observedAttributes(): string[] {
        return ['theme', 'height', 'empty-message', 'loading', 'title'];
    }

    private readonly _root: ShadowRoot;

    // ECharts module ref — set after dynamic import
    private _echartsModule: typeof EChartsNS | null = null;

    // ECharts instance — set after init
    private _chart: EChartsNS.ECharts | null = null;

    // Whether the theme has been registered with ECharts
    private static _themesRegistered = false;

    // ResizeObserver to trigger chart resize
    private _resizeObserver: ResizeObserver | null = null;

    // Pending options to apply once the chart initializes
    private _pendingOptions: EChartsNS.EChartsOption | null = null;

    // Whether options have been set at all (distinguishes null/not-set from
    // null/intentionally cleared)
    private _optionsSet = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    connectedCallback(): void {
        this._doRender();
        this._startLazyLoad();
    }

    disconnectedCallback(): void {
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        if (this._chart) {
            this._chart.dispose();
            this._chart = null;
        }
    }

    attributeChangedCallback(name: string): void {
        if (!this.isConnected) return;

        if (name === 'theme' && this._chart) {
            // Theme change requires re-init
            this._chart.dispose();
            this._chart = null;
            this._initChart();
            return;
        }

        if (name === 'loading') {
            this._doRender();
            if (!this.hasAttribute('loading') && this._chart && this._pendingOptions) {
                this._chart.setOption(this._pendingOptions);
            }
            return;
        }

        this._doRender();

        // Height change: resize the chart if alive
        if (name === 'height' && this._chart) {
            this._chart.resize();
        }
    }

    // ── Public property API ───────────────────────────────────────────────────

    /**
     * Set the full ECharts option object. Triggers a (re)render.
     * Pass `null` to reset to the empty state.
     */
    set options(value: EChartsNS.EChartsOption | null) {
        this._pendingOptions = value;
        this._optionsSet = true;

        if (this._chart) {
            if (value !== null) {
                this._chart.setOption(value, true);
            }
        }
        // Re-render to show/hide empty state
        this._doRender();
    }

    get options(): EChartsNS.EChartsOption | null {
        return this._pendingOptions;
    }

    // ── Lazy loading ──────────────────────────────────────────────────────────

    private _startLazyLoad(): void {
        // Dynamic import — ECharts loads only for pages that use this element.
        // Subsequent imports return the cached module (browser/bundler deduplication).
        void import('echarts').then((mod) => {
            this._echartsModule = mod;
            PkReportEchart._registerThemes(mod);
            this._initChart();
        });
    }

    private static _registerThemes(mod: typeof EChartsNS): void {
        if (PkReportEchart._themesRegistered) return;
        PkReportEchart._themesRegistered = true;
        mod.registerTheme('activitylog-light', activityLogLightTheme);
        mod.registerTheme('activitylog-dark',  activityLogDarkTheme);
    }

    private _initChart(): void {
        if (!this._echartsModule) return;

        const mountEl = this._root.querySelector<HTMLElement>('.echart-mount');
        if (!mountEl) {
            // DOM not ready yet; re-render and retry
            this._doRender();
            requestAnimationFrame(() => this._initChart());
            return;
        }

        const themeName = this.getAttribute('theme') ?? 'activitylog-light';
        this._chart = this._echartsModule.init(mountEl, themeName);

        // Wire ECharts native events → pk-chart-* custom events
        this._chart.on('click', (params) => {
            this.dispatchEvent(new CustomEvent<PkChartEventDetail>('pk-chart-click', {
                bubbles:  true,
                composed: true,
                detail: this._mapParams(params),
            }));
        });

        this._chart.on('mouseover', (params) => {
            this.dispatchEvent(new CustomEvent<PkChartEventDetail>('pk-chart-hover', {
                bubbles:  true,
                composed: true,
                detail: this._mapParams(params),
            }));
        });

        // Apply pending options immediately
        if (this._pendingOptions !== null) {
            this._chart.setOption(this._pendingOptions, true);
        }

        // ResizeObserver: keep chart in sync with container width.
        // Guard with typeof check — jsdom test environment lacks ResizeObserver.
        if (typeof ResizeObserver !== 'undefined') {
            this._resizeObserver = new ResizeObserver(() => {
                this._chart?.resize();
            });
            this._resizeObserver.observe(this);
        }

        // Re-render: removes loading skeleton now that chart is live
        this._doRender();
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    private _mapParams(params: EChartsNS.ECElementEvent): PkChartEventDetail {
        return {
            dataIndex:   typeof params.dataIndex === 'number'   ? params.dataIndex   : -1,
            seriesIndex: typeof params.seriesIndex === 'number' ? params.seriesIndex : -1,
            value:       params.value   ?? null,
            name:        typeof params.name === 'string' ? params.name : '',
            data:        params.data    ?? null,
        };
    }

    private _height(): number {
        const v = parseInt(this.getAttribute('height') ?? '400', 10);
        return isNaN(v) || v <= 0 ? 400 : v;
    }

    private _isLoading(): boolean {
        const v = this.getAttribute('loading');
        return v === '' || v === 'true';
    }

    private _isEmpty(): boolean {
        if (!this._optionsSet || this._pendingOptions === null) return true;
        const series = (this._pendingOptions as Record<string, unknown>)['series'];
        if (Array.isArray(series) && series.length === 0) return true;
        return false;
    }

    // ── Template ──────────────────────────────────────────────────────────────

    private _template(): TemplateResult {
        const height       = this._height();
        const isLoading    = this._isLoading();
        const isEmpty      = this._isEmpty();
        const chartReady   = this._chart !== null && !isLoading && !isEmpty;
        const emptyMessage = this.getAttribute('empty-message') ?? 'No data to display.';
        const titleText    = this.getAttribute('title');

        // Body content — four states:
        // 1. Loading (lazy import in progress OR loading attribute set)
        // 2. Empty (no options / empty series)
        // 3. Chart ready (mount div visible; ECharts renders into it)
        // 4. Pre-init + has options (show mount div; ECharts init pending)
        const body = isLoading
            ? html`
                <div class="echart-loading" style="height:${height}px;">
                    <span class="echart-loading-spinner" aria-hidden="true"></span>
                    <span>Loading chart…</span>
                </div>
              `
            : isEmpty && this._echartsModule !== null
                ? html`
                    <div class="echart-empty" style="height:${height}px;">
                        <slot name="empty-state">
                            <p class="echart-empty-message">${emptyMessage}</p>
                        </slot>
                    </div>
                  `
                : html`
                    <div class="echart-mount"
                         style="height:${height}px;"
                         aria-label=${chartReady ? 'Chart' : 'Chart loading'}
                         role="img">
                    </div>
                    ${!this._echartsModule ? html`
                        <div class="echart-loading"
                             style="position:absolute;inset:0;background:rgba(255,255,255,0.7);">
                            <span class="echart-loading-spinner" aria-hidden="true"></span>
                        </div>
                    ` : nothing}
                  `;

        return html`
            <style>${sharedTokens}${panelStyles}</style>
            <div class="echart-panel">
                ${titleText ? html`
                    <div class="echart-header">
                        <p class="echart-title">${titleText}</p>
                    </div>
                ` : nothing}
                <div class="echart-container">
                    ${body}
                </div>
            </div>
        `;
    }

    private _doRender(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportEchart.tagName)) {
    customElements.define(PkReportEchart.tagName, PkReportEchart);
}

// ─────────────────────────────────────────────────────────────────────────────

declare global {
    interface HTMLElementTagNameMap {
        'pk-report-echart': PkReportEchart;
    }
}
